<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import {
  onClickOutside,
  useBreakpoints,
  breakpointsTailwind,
} from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';
import {
  buildDueIso,
  dueDateKey,
  recurrenceEndsToIso,
  recurrenceEndsToPicker,
  splitDue,
} from '../helpers/dueDate';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import ContactAPI from 'dashboard/api/contacts';
import KanbanAPI from 'dashboard/api/kanban';
import { PRIORITY_META, PRIORITY_ORDER } from '../constants/priorityColors';
import TaskDatePicker from './TaskDatePicker.vue';
import SmartTitleInput from './SmartTitleInput.vue';

const props = defineProps({
  task: { type: Object, required: true },
  selectionMode: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  hideContact: { type: Boolean, default: false },
  hideTags: { type: Boolean, default: false },
  compactPriority: { type: Boolean, default: false },
  hideCompletedInfo: { type: Boolean, default: false },
  // barras laterais (contato, conversa): os breakpoints md: olham a largura da
  // tela, não do container, e o título encolhia até sumir ao lado do prazo
  stacked: { type: Boolean, default: false },
});

const emit = defineEmits([
  'open',
  'complete',
  'updated',
  'select',
  'openWithWarning',
]);

const { t } = useI18n();
const { updateTask } = useTaskActions();
const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');

// ── inline edit: título ──────────────────────────────────────────
const titleRef = ref(null);
const editingTitle = ref(false);
const titleDraft = ref('');
const smartPendingFields = ref({});

function startEditTitle(e) {
  e.stopPropagation();
  titleDraft.value = props.task.title;
  smartPendingFields.value = {};
  editingTitle.value = true;
  const clickX = e.clientX;
  const clickY = e.clientY;
  nextTick(() => {
    const el = titleRef.value?.$el ?? titleRef.value;
    if (!el) return;
    const editorEl = el.querySelector('[contenteditable]') ?? el;
    editorEl.focus();
    const range =
      document.caretRangeFromPoint?.(clickX, clickY) ??
      (() => {
        const pos = document.caretPositionFromPoint?.(clickX, clickY);
        if (!pos) return null;
        const r = document.createRange();
        r.setStart(pos.offsetNode, pos.offset);
        r.collapse(true);
        return r;
      })();
    if (range && editorEl.contains(range.startContainer)) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  });
}

function onSmartPendingFields(fields) {
  smartPendingFields.value = fields;
}

async function commitTitle() {
  editingTitle.value = false;
  const fields = smartPendingFields.value;
  const saves = [];
  if (titleDraft.value.trim() && titleDraft.value !== props.task.title) {
    saves.push(
      updateTask({
        taskId: props.task.id,
        title: titleDraft.value.trim(),
      })
    );
  }
  if (fields.description !== null) {
    saves.push(
      updateTask({
        taskId: props.task.id,
        description: fields.description,
      })
    );
  }
  if (fields.priority) {
    saves.push(
      updateTask({
        taskId: props.task.id,
        priority: fields.priority,
      })
    );
  }
  if (fields.assignee_id) {
    saves.push(
      updateTask({
        taskId: props.task.id,
        assignee_id: fields.assignee_id,
      })
    );
  }
  if (fields.task_type) {
    saves.push(
      updateTask({
        taskId: props.task.id,
        task_type: fields.task_type,
      })
    );
  }
  if (fields.label_list?.length) {
    const existing = props.task.label_list ?? [];
    const merged = [...new Set([...existing, ...fields.label_list])];
    saves.push(
      updateTask({
        taskId: props.task.id,
        label_list: merged,
      })
    );
  }
  if (fields.due_date) {
    const iso = buildDueIso(fields.due_date, fields.due_time);
    saves.push(
      updateTask({
        taskId: props.task.id,
        due_date: iso,
      })
    );
  }
  if (fields.contact_id) {
    saves.push(
      updateTask({
        taskId: props.task.id,
        contact_id: fields.contact_id,
      })
    );
  }
  if (saves.length) {
    await Promise.all(saves);
    emit('updated');
  }
  smartPendingFields.value = {};
}

// ── estado de menus (declarados juntos para closeMenus poder referenciar todos) ──
const showDatePicker = ref(false);
const datePickerAnchor = ref(null);
const datePickerContainer = ref(null);
const datePickerRef = ref(null);
const datePickerPos = ref({ top: 0, left: 0 });

function calcDatePickerPos() {
  if (!datePickerAnchor.value) return;
  const rect = datePickerAnchor.value.getBoundingClientRect();
  const pickerW = 288; // w-72
  const pickerH = 420; // estimativa da altura do picker
  const margin = 8;
  let top = rect.bottom + margin;
  let left = rect.right - pickerW;
  if (left < margin) left = margin;
  if (left + pickerW > window.innerWidth - margin)
    left = window.innerWidth - pickerW - margin;
  if (top + pickerH > window.innerHeight - margin)
    top = rect.top - pickerH - margin;
  if (top < margin) top = margin;
  datePickerPos.value = { top, left };
}

function calcFlip(triggerEl, dropdownWidthPx, defaultSide = 'left') {
  if (!triggerEl) return defaultSide;
  const rect = triggerEl.getBoundingClientRect();
  const margin = 8;
  if (defaultSide === 'left') {
    // dropdown ancorado à esquerda cresce p/ direita — checa overflow direito
    return rect.left + dropdownWidthPx > window.innerWidth - margin
      ? 'right'
      : 'left';
  }
  // dropdown ancorado à direita cresce p/ esquerda — checa overflow esquerdo
  return rect.right - dropdownWidthPx < margin ? 'left' : 'right';
}

const priorityAlign = ref('right'); // já é right-0 hoje, mantém default
const typeAlign = ref('right'); // já é right-0 hoje, mantém default
const labelAlign = ref('left'); // já é left-0 hoje, mantém default

const showAssigneeMenu = ref(false);
const showTypeMenu = ref(false);
const showPriorityMenu = ref(false);
const showLabelMenu = ref(false);
const editingContact = ref(false);
const showContactDropdown = ref(false);
const isSelectingContact = ref(false);

function closeMenus() {
  showAssigneeMenu.value = false;
  showTypeMenu.value = false;
  showPriorityMenu.value = false;
  showLabelMenu.value = false;
  showDatePicker.value = false;
  if (!isSelectingContact.value) {
    editingContact.value = false;
    showContactDropdown.value = false;
  }
}

const datePickerValue = computed(() => ({
  ...splitDue(props.task.due_date),
  recurrence_type: props.task.recurrence_type ?? null,
  recurrence_interval: props.task.recurrence_interval ?? null,
  recurrence_unit: props.task.recurrence_unit ?? null,
  recurrence_ends_at: recurrenceEndsToPicker(props.task.recurrence_ends_at),
  recurrence_count: props.task.recurrence_count ?? null,
}));

const recurrenceEndsIso = () =>
  props.task.recurrence_ends_at
    ? new Date(props.task.recurrence_ends_at * 1000).toISOString()
    : null;

function onDocumentClick(e) {
  if (!document.contains(e.target)) return;
  if (
    datePickerContainer.value?.contains(e.target) ||
    datePickerAnchor.value?.contains(e.target)
  )
    return;
  datePickerRef.value?.save();
  showDatePicker.value = false;
  document.removeEventListener('click', onDocumentClick);
}

function startEditDue(e) {
  if (props.selectionMode) return;
  e.stopPropagation();
  const next = !showDatePicker.value;
  closeMenus();
  showDatePicker.value = next;
  if (next) {
    nextTick(() => {
      calcDatePickerPos();
      // Aguarda o v-if montar o container antes de registrar o listener
      nextTick(() => {
        document.addEventListener('click', onDocumentClick);
      });
    });
  } else {
    document.removeEventListener('click', onDocumentClick);
  }
}

async function commitDue(val) {
  // eslint-disable-next-line no-underscore-dangle
  if (val._close) {
    showDatePicker.value = false;
    document.removeEventListener('click', onDocumentClick);
  }
  await updateTask({
    taskId: props.task.id,
    due_date: buildDueIso(val.due_date, val.due_time),
    recurrence_type: val.recurrence_type ?? null,
    recurrence_interval: val.recurrence_interval ?? null,
    recurrence_unit: val.recurrence_unit ?? null,
    recurrence_ends_at: recurrenceEndsToIso(val.recurrence_ends_at),
    recurrence_count: val.recurrence_count ?? null,
  });
  emit('updated');
}

async function clearDueTime() {
  await updateTask({
    taskId: props.task.id,
    due_date: props.task.due_date
      ? buildDueIso(dueDateKey(props.task.due_date))
      : null,
    recurrence_type: props.task.recurrence_type ?? null,
    recurrence_interval: props.task.recurrence_interval ?? null,
    recurrence_unit: props.task.recurrence_unit ?? null,
    recurrence_ends_at: recurrenceEndsIso(),
    recurrence_count: props.task.recurrence_count ?? null,
  });
  emit('updated');
}

// ── assignee dropdown ────────────────────────────────────────────
const assigneeSearch = ref('');
const assigneeHighlight = ref(0);

const filteredAssigneeAgents = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q) return agents.value ?? [];
  return (agents.value ?? [])
    .filter(a => a.name.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.name.toLowerCase().indexOf(q) - b.name.toLowerCase().indexOf(q)
    );
});

const UNASSIGN_LABEL = 'remover responsável';
const assigneeNavigable = computed(() => {
  const q = assigneeSearch.value.toLowerCase();
  if (!q)
    return [{ id: null }, ...(agents.value ?? []).map(a => ({ id: a.id }))];
  const items = filteredAssigneeAgents.value.map(a => ({ id: a.id }));
  const showUnassign = UNASSIGN_LABEL.includes(q);
  return [...(showUnassign ? [{ id: null }] : []), ...items];
});

watch(assigneeNavigable, () => {
  assigneeHighlight.value = 0;
});
watch(assigneeSearch, () => {
  assigneeHighlight.value = 0;
});

function toggleAssigneeMenu(e) {
  e.stopPropagation();
  const next = !showAssigneeMenu.value;
  closeMenus();
  showAssigneeMenu.value = next;
  if (next) {
    assigneeSearch.value = '';
    const currentId = props.task.assignee?.id ?? null;
    const idx = assigneeNavigable.value.findIndex(i => i.id === currentId);
    assigneeHighlight.value = idx >= 0 ? idx : 0;
  }
}

async function selectAssignee(agent) {
  showAssigneeMenu.value = false;
  assigneeSearch.value = '';
  await updateTask({
    taskId: props.task.id,
    assignee_id: agent?.id ?? null,
  });
  emit('updated');
}

const isResolvingLeader = ref(false);
async function resolveLeader() {
  isResolvingLeader.value = true;
  showAssigneeMenu.value = false;
  assigneeSearch.value = '';
  try {
    const cardId = props.task.kanban_card_id ?? null;
    if (cardId) {
      const { data } = await KanbanAPI.getCard(cardId);
      const card = data?.data ?? data ?? null;
      const agentId = card?.assignee?.id ?? card?.assignee_id ?? null;
      if (agentId) {
        const agent = (agents.value ?? []).find(a => a.id === agentId);
        if (agent) {
          await selectAssignee(agent);
          return;
        }
      }
    }
    const contactId = props.task.contact?.id ?? null;
    if (contactId) {
      const { data } = await ContactAPI.getConversations(contactId);
      const conversations = data?.payload ?? [];
      if (conversations.length) {
        const last = conversations.reduce((a, b) => (a.id > b.id ? a : b));
        const agentId = last.meta?.assignee?.id ?? null;
        if (agentId) {
          const agent = (agents.value ?? []).find(a => a.id === agentId);
          if (agent) {
            await selectAssignee(agent);
          }
        }
      }
    }
  } finally {
    isResolvingLeader.value = false;
  }
}

function onAssigneeKeydown(e) {
  if (!showAssigneeMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    assigneeHighlight.value = Math.min(
      assigneeHighlight.value + 1,
      assigneeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    assigneeHighlight.value = Math.max(assigneeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = assigneeNavigable.value[assigneeHighlight.value];
    if (item) {
      const agent =
        item.id === null
          ? null
          : (agents.value ?? []).find(a => a.id === item.id);
      selectAssignee(agent ?? null);
    }
  } else if (e.key === 'Escape') {
    showAssigneeMenu.value = false;
    assigneeSearch.value = '';
  } else if (e.key === 'Backspace') {
    assigneeSearch.value = assigneeSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    assigneeSearch.value += e.key;
  }
}

watch(showAssigneeMenu, val => {
  if (val) window.addEventListener('keydown', onAssigneeKeydown);
  else {
    window.removeEventListener('keydown', onAssigneeKeydown);
    assigneeSearch.value = '';
  }
});

const TASK_TYPES = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-circle-dot' },
  { key: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { key: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { key: 'contract', label: 'Contrato', icon: 'i-lucide-file-signature' },
  { key: 'payment', label: 'Pagamento', icon: 'i-lucide-banknote' },
];

const localTaskType = ref(props.task.task_type ?? 'general');
watch(
  () => props.task.task_type,
  v => {
    localTaskType.value = v ?? 'general';
  }
);
const currentType = computed(
  () =>
    TASK_TYPES.find(type => type.key === localTaskType.value) ?? TASK_TYPES[0]
);

const typeSearch = ref('');
const typeHighlight = ref(0);

const filteredTaskTypes = computed(() => {
  const q = typeSearch.value.toLowerCase();
  if (!q) return TASK_TYPES;
  return TASK_TYPES.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const typeNavigable = computed(() =>
  filteredTaskTypes.value.map(o => ({ id: o.key }))
);

watch(typeNavigable, () => {
  typeHighlight.value = 0;
});
watch(typeSearch, () => {
  typeHighlight.value = 0;
});

function toggleTypeMenu(e) {
  e.stopPropagation();
  const next = !showTypeMenu.value;
  closeMenus();
  showTypeMenu.value = next;
  if (next) {
    typeAlign.value = calcFlip(e.currentTarget, 160, 'right'); // w-40
    typeSearch.value = '';
    const idx = TASK_TYPES.findIndex(tp => tp.key === props.task.task_type);
    typeHighlight.value = idx >= 0 ? idx : 0;
  }
}

async function selectType(type) {
  showTypeMenu.value = false;
  typeSearch.value = '';
  const previous = localTaskType.value;
  localTaskType.value = type.key;
  const result = await updateTask({
    taskId: props.task.id,
    task_type: type.key,
  });
  if (!result.success) localTaskType.value = previous;
  else emit('updated');
}

function onTypeKeydown(e) {
  if (!showTypeMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    typeHighlight.value = Math.min(
      typeHighlight.value + 1,
      typeNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    typeHighlight.value = Math.max(typeHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = typeNavigable.value[typeHighlight.value];
    if (item) selectType(TASK_TYPES.find(tp => tp.key === item.id));
  } else if (e.key === 'Escape') {
    showTypeMenu.value = false;
    typeSearch.value = '';
  } else if (e.key === 'Backspace') {
    typeSearch.value = typeSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    typeSearch.value += e.key;
  }
}

watch(showTypeMenu, val => {
  if (val) window.addEventListener('keydown', onTypeKeydown);
  else {
    window.removeEventListener('keydown', onTypeKeydown);
    typeSearch.value = '';
  }
});

// ── prioridade inline ──────────────────────────────────────────────
const PRIORITY_LABELS = {
  urgent: 'Urgente',
  high: 'Prioridade',
  normal: 'Normal',
  no_priority: 'Baixa',
};

const PRIORITY_OPTIONS = PRIORITY_ORDER.map(key => {
  const meta = PRIORITY_META[key];
  return {
    key,
    label: PRIORITY_LABELS[key],
    bg: meta.bg ?? '',
    text: meta.text ?? '',
    dot: meta.dot ?? '',
    bgColor: meta.bgColor,
    textColor: meta.textColor,
  };
});

const localPriority = ref(props.task.priority ?? null);
watch(
  () => props.task.priority,
  v => {
    localPriority.value = v ?? null;
  }
);
const currentPriority = computed(
  () => PRIORITY_OPTIONS.find(p => p.key === localPriority.value) ?? null
);

const prioritySearch = ref('');
const priorityHighlight = ref(0);

const filteredPriorityOptions = computed(() => {
  const q = prioritySearch.value.toLowerCase();
  if (!q) return PRIORITY_OPTIONS;
  return PRIORITY_OPTIONS.filter(o => o.label.toLowerCase().includes(q)).sort(
    (a, b) =>
      a.label.toLowerCase().indexOf(q) - b.label.toLowerCase().indexOf(q)
  );
});

const priorityNavigable = computed(() =>
  filteredPriorityOptions.value.map(o => ({ id: o.key }))
);

watch(priorityNavigable, () => {
  priorityHighlight.value = 0;
});
watch(prioritySearch, () => {
  priorityHighlight.value = 0;
});

function togglePriorityMenu(e) {
  e.stopPropagation();
  const next = !showPriorityMenu.value;
  closeMenus();
  showPriorityMenu.value = next;
  if (next) {
    priorityAlign.value = calcFlip(e.currentTarget, 144, 'right'); // w-36
    prioritySearch.value = '';
    const idx = PRIORITY_OPTIONS.findIndex(p => p.key === props.task.priority);
    priorityHighlight.value = idx >= 0 ? idx : 0;
  }
}

async function selectPriority(key) {
  showPriorityMenu.value = false;
  prioritySearch.value = '';
  const previous = localPriority.value;
  localPriority.value = key;
  const result = await updateTask({
    taskId: props.task.id,
    priority: key,
  });
  if (!result.success) localPriority.value = previous;
  else emit('updated');
}

function onPriorityKeydown(e) {
  if (!showPriorityMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    priorityHighlight.value = Math.min(
      priorityHighlight.value + 1,
      priorityNavigable.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    priorityHighlight.value = Math.max(priorityHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = priorityNavigable.value[priorityHighlight.value];
    if (item) selectPriority(item.id);
  } else if (e.key === 'Escape') {
    showPriorityMenu.value = false;
    prioritySearch.value = '';
  } else if (e.key === 'Backspace') {
    prioritySearch.value = prioritySearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    prioritySearch.value += e.key;
  }
}

watch(showPriorityMenu, val => {
  if (val) window.addEventListener('keydown', onPriorityKeydown);
  else {
    window.removeEventListener('keydown', onPriorityKeydown);
    prioritySearch.value = '';
  }
});

// ── tags inline (labels nativas) ───────────────────────────────────
const labelSearch = ref('');
const labelHighlight = ref(0);

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  if (!q) return accountLabels.value ?? [];
  return (accountLabels.value ?? [])
    .filter(l => l.title.toLowerCase().includes(q))
    .sort(
      (a, b) =>
        a.title.toLowerCase().indexOf(q) - b.title.toLowerCase().indexOf(q)
    );
});

watch(filteredLabels, () => {
  labelHighlight.value = 0;
});
watch(labelSearch, () => {
  labelHighlight.value = 0;
});

function isLabelActive(title) {
  return (props.task.label_list ?? []).includes(title);
}

async function toggleLabel(title, e) {
  e.stopPropagation();
  const existing = props.task.label_list ?? [];
  const updated = existing.includes(title)
    ? existing.filter(l => l !== title)
    : [...existing, title];
  await updateTask({
    taskId: props.task.id,
    label_list: updated,
  });
  emit('updated');
}

async function removeTag(tag, e) {
  e.stopPropagation();
  const updated = (props.task.label_list ?? []).filter(l => l !== tag);
  await updateTask({
    taskId: props.task.id,
    label_list: updated,
  });
  emit('updated');
}

function openLabelMenu(e) {
  e.stopPropagation();
  const next = !showLabelMenu.value;
  closeMenus();
  showLabelMenu.value = next;
  if (next) labelAlign.value = calcFlip(e.currentTarget, 176, 'left'); // w-44
  labelSearch.value = '';
  labelHighlight.value = 0;
}

function onLabelKeydown(e) {
  if (!showLabelMenu.value) return;
  e.stopImmediatePropagation();
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    labelHighlight.value = Math.min(
      labelHighlight.value + 1,
      filteredLabels.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    labelHighlight.value = Math.max(labelHighlight.value - 1, 0);
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const lbl = filteredLabels.value[labelHighlight.value];
    if (lbl) toggleLabel(lbl.title, e);
  } else if (e.key === 'Escape') {
    showLabelMenu.value = false;
    labelSearch.value = '';
  } else if (e.key === 'Backspace') {
    labelSearch.value = labelSearch.value.slice(0, -1);
  } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
    labelSearch.value += e.key;
  }
}

watch(showLabelMenu, val => {
  if (val) window.addEventListener('keydown', onLabelKeydown);
  else {
    window.removeEventListener('keydown', onLabelKeydown);
    labelSearch.value = '';
  }
});

// ── tags coloridas ───────────────────────────────────────────────
const TAG_COLORS = {
  proposta: { bg: 'rgba(59,130,246,0.12)', text: '#1d4ed8' },
  proposal: { bg: 'rgba(59,130,246,0.12)', text: '#1d4ed8' },
  'follow-up': { bg: 'rgba(249,115,22,0.12)', text: '#c2410c' },
  followup: { bg: 'rgba(249,115,22,0.12)', text: '#c2410c' },
  suporte: { bg: 'rgba(16,185,129,0.12)', text: '#065f46' },
  support: { bg: 'rgba(16,185,129,0.12)', text: '#065f46' },
  urgente: { bg: 'rgba(239,68,68,0.12)', text: '#b91c1c' },
  urgent: { bg: 'rgba(239,68,68,0.12)', text: '#b91c1c' },
  contrato: { bg: 'rgba(107,114,128,0.12)', text: '#374151' },
  contract: { bg: 'rgba(107,114,128,0.12)', text: '#374151' },
  reuniao: { bg: 'rgba(139,92,246,0.12)', text: '#6d28d9' },
  meeting: { bg: 'rgba(139,92,246,0.12)', text: '#6d28d9' },
};

const DEFAULT_TAG_COLOR = { bg: 'rgba(107,114,128,0.1)', text: '#4b5563' };

function tagStyle(label) {
  const accountLabel = (accountLabels.value ?? []).find(
    l => l.title?.toLowerCase() === label.toLowerCase()
  );
  if (accountLabel?.color) {
    const hex = accountLabel.color;
    return { bg: `${hex}22`, text: hex };
  }
  const key = label.toLowerCase().replace(/[_\s]/g, '-');
  return (
    TAG_COLORS[key] ?? TAG_COLORS[label.toLowerCase()] ?? DEFAULT_TAG_COLOR
  );
}

// ── avatar iniciais (2 letras) ───────────────────────────────────
const { avatarColor, initials: agentInitials } = useAvatarColor();
function initials(name) {
  return agentInitials(name ?? '') || '?';
}

// ── utilidades ───────────────────────────────────────────────────
const PRIORITY_BAR = Object.fromEntries(
  PRIORITY_ORDER.map(key => {
    const meta = PRIORITY_META[key];
    return [
      key,
      meta.barStyle ? { style: meta.barStyle } : { class: meta.bar },
    ];
  })
);

const priorityBarClass = computed(
  () => PRIORITY_BAR[props.task.priority]?.class ?? ''
);
const priorityBarStyle = computed(
  () => PRIORITY_BAR[props.task.priority]?.style ?? {}
);

const displayName = computed(
  () => props.task.contact?.company_name || props.task.contact?.name || ''
);

// edição inline de contato
const contactInputRef = ref(null);
const contactSearch = ref('');
const contactResults = ref([]);
const contactHighlightedIndex = ref(0);

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

async function searchContacts(query) {
  if (!query || query.length < 1) {
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlightedIndex.value = 0;
    showContactDropdown.value = contactResults.value.length > 0;
  } catch {
    contactResults.value = [];
  }
}

function cancelContactEdit() {
  if (isSelectingContact.value) return;
  editingContact.value = false;
  contactSearch.value = '';
  contactResults.value = [];
  showContactDropdown.value = false;
}

async function selectContactInline(contact) {
  isSelectingContact.value = false;
  showContactDropdown.value = false;
  editingContact.value = false;
  await updateTask({
    taskId: props.task.id,
    contact_id: contact.id,
  });
  emit('updated');
}

function onContactKeydown(e) {
  if (!showContactDropdown.value) return;
  if (e.key === 'Escape') {
    cancelContactEdit();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.min(
      contactHighlightedIndex.value + 1,
      contactResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.max(
      contactHighlightedIndex.value - 1,
      0
    );
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = contactResults.value[contactHighlightedIndex.value];
    if (item) selectContactInline(item);
  }
}

function openContactEdit() {
  closeMenus();
  contactSearch.value = displayName.value;
  editingContact.value = true;
  nextTick(() => {
    contactInputRef.value?.focus();
    contactInputRef.value?.select();
  });
}

const dueDateStatus = computed(() => {
  if (!props.task.due_date) return null;
  const due = new Date(props.task.due_date * 1000);
  const now = new Date();
  const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dueDayLocal = new Date(
    due.getFullYear(),
    due.getMonth(),
    due.getDate()
  );
  const diff = (dueDayLocal - todayLocal) / 86400000;
  if (props.task.status === 'completed') return 'done';
  if (diff < 0) return 'overdue';
  if (diff === 0) {
    const h = due.getHours();
    const m = due.getMinutes();
    const hasTime = h !== 0 || m !== 0;
    if (hasTime && due < now) return 'overdue';
    return 'today';
  }
  if (diff === 1) return 'tomorrow';
  return 'normal';
});

const dueDateColor = computed(
  () =>
    ({
      overdue: 'text-n-ruby-11 font-medium',
      today: 'text-n-teal-11 font-medium',
      tomorrow: 'text-n-amber-11 font-medium',
      normal: 'text-n-slate-10',
      done: 'text-n-slate-10',
    })[dueDateStatus.value] ?? 'text-n-slate-10'
);

const formattedDue = computed(() => {
  if (!props.task.due_date) return null;
  const due = new Date(props.task.due_date * 1000);
  const h = due.getHours();
  const m = due.getMinutes();
  const hasTime = h !== 0 || m !== 0;
  const timePart = hasTime
    ? ` ${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    : '';
  if (dueDateStatus.value === 'today' || dueDateStatus.value === 'overdue') {
    const nowLocal = new Date();
    const todayLocal = `${nowLocal.getFullYear()}-${nowLocal.getMonth()}-${nowLocal.getDate()}`;
    const dueLocal = `${due.getFullYear()}-${due.getMonth()}-${due.getDate()}`;
    if (todayLocal === dueLocal) return `Hoje${timePart}`;
  }
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  return `${due.getDate()} ${months[due.getMonth()]}${timePart}`;
});

// Badge "✓ Concluída Jun 24" para tarefas completed
const completedAtLabel = computed(() => {
  if (props.task.status !== 'completed') return null;
  const ts = props.task.completed_at ?? props.task.updated_at;
  if (!ts) return t('TASKS.STATUS.COMPLETED');
  const d = new Date(ts * 1000);
  const formatted = d.toLocaleDateString('pt-BR', {
    month: 'short',
    day: 'numeric',
  });
  return `✓ ${t('TASKS.STATUS.COMPLETED')} ${formatted}`;
});

const descPreview = computed(() => {
  if (!props.task.description) return null;
  return props.task.description.slice(0, 200);
});

// ── checklist ────────────────────────────────────────────────────
const checklistProgress = computed(() => {
  const items = props.task.checklist_items ?? [];
  if (!items.length) return null;
  const done = items.filter(i => i.completed).length;
  return { done, total: items.length };
});

const checklistSecondaryAgent = computed(() => {
  const items = props.task.checklist_items ?? [];
  const mainId = props.task.assignee?.id ?? null;
  const item = items.find(i => i.assignee_id && i.assignee_id !== mainId);
  if (!item) return null;
  return (agents.value ?? []).find(a => a.id === item.assignee_id) ?? null;
});

function toggleComplete(e) {
  e.stopPropagation();
  if (props.selectionMode) return;
  const checklistItems = props.task.checklist_items ?? [];
  if (checklistItems.length > 0 && checklistItems.some(i => !i.completed)) {
    emit('openWithWarning', props.task);
    return;
  }
  emit('complete', props.task);
}

function openTask(e) {
  e.stopPropagation();
  if (props.selectionMode) return;
  emit('open', props.task);
}

function onItemClick(e) {
  if (props.selectionMode) {
    e.stopPropagation();
    emit('select', props.task.id);
    return;
  }
  closeMenus();
  if (isMobile.value) {
    openTask(e);
  }
}

watch(showContactDropdown, val => {
  if (val) window.addEventListener('keydown', onContactKeydown);
  else window.removeEventListener('keydown', onContactKeydown);
});

const itemRef = ref(null);
onClickOutside(itemRef, closeMenus, { ignore: [datePickerContainer] });
onUnmounted(() => {
  window.removeEventListener('keydown', onPriorityKeydown);
  window.removeEventListener('keydown', onAssigneeKeydown);
  window.removeEventListener('keydown', onTypeKeydown);
  window.removeEventListener('keydown', onLabelKeydown);
  window.removeEventListener('keydown', onContactKeydown);
  document.removeEventListener('click', onDocumentClick);
});
</script>

<template>
  <div
    ref="itemRef"
    class="task-drag-item flex flex-col border-b border-n-weak group relative min-h-[52px] transition-colors"
    :class="[
      stacked ? '' : 'md:flex-row md:items-stretch',
      hideContact ? '' : 'ml-2',
      selectionMode
        ? 'cursor-pointer'
        : 'cursor-grab active:cursor-grabbing hover:bg-n-alpha-1',
      selectionMode && selected
        ? 'bg-n-alpha-2'
        : selectionMode && task.status === 'completed'
          ? 'bg-n-teal-2'
          : selectionMode
            ? 'bg-n-alpha-1'
            : '',
    ]"
    @click="onItemClick"
    @dblclick="openTask"
  >
    <div class="flex items-center" :class="{ 'md:contents': !stacked }">
      <!-- Borda lateral de prioridade -->
      <span
        v-if="!hideContact"
        class="w-1 flex-shrink-0 self-stretch rounded-sm"
        :class="priorityBarClass"
        :style="priorityBarStyle"
      />

      <!-- Grip de drag -->
      <span
        v-if="!hideContact"
        class="flex-shrink-0 self-center px-1 transition-opacity text-n-slate-9"
        :class="
          selectionMode
            ? 'opacity-0 pointer-events-none'
            : 'opacity-0 group-hover:opacity-40'
        "
      >
        <span class="i-lucide-grip-vertical size-4" />
      </span>

      <!-- Checkbox -->
      <button
        v-if="!hideContact"
        class="flex-shrink-0 rounded self-center mr-3 flex items-center justify-center transition-all"
        :class="
          selectionMode
            ? selected
              ? 'bg-n-brand'
              : 'hover:border-n-brand hover:bg-n-alpha-2'
            : task.status === 'completed'
              ? 'bg-n-teal-9'
              : 'hover:border-n-teal-6 hover:bg-n-teal-2'
        "
        :style="
          (!selectionMode && task.status === 'completed') ||
          (selectionMode && selected)
            ? 'width:16px;height:16px;min-width:16px;padding:0'
            : 'width:16px;height:16px;min-width:16px;padding:0;border:1px solid rgb(217,217,224)'
        "
        @click.stop="
          selectionMode ? emit('select', task.id) : toggleComplete($event)
        "
      >
        <span
          v-if="
            (selectionMode && selected) ||
            (!selectionMode && task.status === 'completed')
          "
          class="i-lucide-check size-2.5 text-white"
        />
      </button>

      <!-- Checkbox compacto (modo kanban — alinhado ao texto) -->
      <button
        v-if="hideContact"
        class="absolute top-1/2 -translate-y-1/2 left-8 size-4 min-w-4 rounded flex items-center justify-center transition-all p-0"
        :class="
          task.status === 'completed'
            ? 'bg-n-teal-9'
            : 'border border-n-slate-6 hover:border-n-teal-6 hover:bg-n-teal-2'
        "
        @click.stop="toggleComplete($event)"
      >
        <span
          v-if="task.status === 'completed'"
          class="i-lucide-check size-2.5 text-white"
        />
      </button>

      <!-- ESQUERDA: título + sublinha -->
      <div
        class="flex-1 min-w-0 py-2 pr-2 flex flex-col justify-center"
        :class="hideContact ? 'pl-[52px]' : ''"
      >
        <!-- Título: duplo clique para editar com SmartTitleInput -->
        <SmartTitleInput
          v-if="editingTitle"
          ref="titleRef"
          v-model="titleDraft"
          autofocus
          class="text-sm font-medium text-n-slate-12"
          @update:pending-fields="onSmartPendingFields"
          @blur="commitTitle"
          @submit="commitTitle"
          @cancel="editingTitle = false"
          @click.stop
        />
        <span
          v-else
          class="text-sm font-medium truncate select-none leading-tight"
          :class="
            task.status === 'completed'
              ? 'line-through text-n-slate-10'
              : 'text-n-slate-12'
          "
          @dblclick="startEditTitle"
        >
          {{ task.title }}
        </span>

        <!-- Sublinha: nome · data criação · tags · badge concluída -->
        <div class="flex items-center flex-wrap gap-1 overflow-visible min-h-6">
          <!-- Contato clicável / editável inline -->
          <div
            v-if="!hideContact"
            class="relative shrink-0 inline-flex items-center h-6"
            @click.stop
            @dblclick.stop
          >
            <input
              v-if="editingContact"
              ref="contactInputRef"
              v-model="contactSearch"
              class="text-xs leading-none text-n-slate-12 bg-transparent outline-none focus:outline-none border-0 w-32 p-0"
              :placeholder="$t('TASKS.FORM.CONTACT_PLACEHOLDER')"
              @input="searchContacts($event.target.value)"
            />
            <button
              v-else
              class="inline-flex items-center h-6 text-xs leading-none text-n-slate-11 hover:text-n-slate-12 transition-colors p-0 text-left"
              @click="openContactEdit"
            >
              {{ displayName || t('TASKS.FORM.CONTACT_PLACEHOLDER') }}
            </button>
            <div
              v-if="showContactDropdown && contactResults.length"
              class="absolute left-0 top-6 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-44 overflow-y-auto w-52"
              @mousedown="isSelectingContact = true"
            >
              <button
                v-for="(c, idx) in contactResults"
                :key="c.id"
                type="button"
                class="w-full text-left px-3 py-2 text-xs transition-colors"
                :class="
                  idx === contactHighlightedIndex
                    ? 'text-n-brand font-medium bg-n-alpha-1'
                    : 'text-n-slate-12 hover:bg-n-alpha-1'
                "
                @click="selectContactInline(c)"
              >
                <span>{{ c.name }}</span>
                <span v-if="c.company_name" class="text-n-slate-10 ml-1">
                  · {{ c.company_name }}
                </span>
              </button>
            </div>
          </div>

          <span
            v-if="!hideContact"
            class="inline-flex items-center h-6 text-n-container text-xs leading-none shrink-0"
            >·</span
          >

          <span
            v-if="task.status !== 'completed' || !hideCompletedInfo"
            class="inline-flex items-center h-6 text-xs leading-none text-n-slate-10 shrink-0"
          >
            {{
              new Date(task.created_at * 1000).toLocaleDateString('pt-BR', {
                month: 'short',
                day: 'numeric',
              })
            }}
          </span>

          <!-- Badge "Concluída" sempre antes das tags -->
          <span
            v-if="completedAtLabel"
            class="inline-flex items-center h-6 text-[11px] font-medium text-n-teal-11 shrink-0"
          >
            {{ completedAtLabel }}
          </span>

          <!-- Labels nativas -->
          <template
            v-if="
              task.label_list?.length &&
              (task.status !== 'completed' || !hideCompletedInfo) &&
              !hideTags
            "
          >
            <span
              class="inline-flex items-center h-6 text-n-container text-xs leading-none shrink-0"
              >·</span
            >
            <span
              v-for="label in task.label_list"
              :key="label"
              class="group/tag inline-flex items-center h-5 text-[11px] leading-none font-medium px-1.5 rounded shrink-0"
              :style="{
                backgroundColor: tagStyle(label).bg,
                color: tagStyle(label).text,
              }"
            >
              {{ label }}
              <span
                class="overflow-hidden transition-all duration-150 max-w-0 group-hover/tag:max-w-[12px] flex items-center justify-center"
              >
                <button
                  class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
                  @click="removeTag(label, $event)"
                >
                  <span class="i-lucide-x size-[8px] flex-shrink-0" />
                </button>
              </span>
            </span>
          </template>

          <!-- Botão adicionar label + dropdown -->
          <div
            v-if="
              (task.status !== 'completed' || !hideCompletedInfo) && !hideTags
            "
            class="relative shrink-0"
            @click.stop
            @dblclick.stop
          >
            <button
              class="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity focus:outline-none"
              :class="
                showLabelMenu && labelSearch ? 'opacity-100 !opacity-100' : ''
              "
              @click="openLabelMenu"
            >
              <span
                v-if="showLabelMenu && labelSearch"
                class="text-[9px] font-medium text-n-slate-10"
                >{{ labelSearch }}
              </span>
              <span v-else class="i-lucide-tag size-3 text-n-slate-10" />
            </button>
            <div
              v-if="showLabelMenu"
              class="absolute top-5 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden py-1"
              :class="labelAlign === 'left' ? 'left-0' : 'right-0'"
              @click.stop
            >
              <button
                v-for="(lbl, idx) in filteredLabels"
                :key="lbl.id"
                class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors focus:outline-none"
                :class="
                  idx === labelHighlight
                    ? 'bg-n-alpha-1 text-n-slate-12 font-medium'
                    : isLabelActive(lbl.title)
                      ? 'text-n-slate-12 font-medium'
                      : 'text-n-slate-11'
                "
                @click="toggleLabel(lbl.title, $event)"
                @mouseenter="labelHighlight = idx"
              >
                <span
                  class="size-2.5 rounded-full flex-shrink-0"
                  :style="{ backgroundColor: lbl.color }"
                />
                <span class="truncate">{{ lbl.title }}</span>
                <span
                  v-if="isLabelActive(lbl.title)"
                  class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
                />
              </button>
              <p
                v-if="filteredLabels.length === 0"
                class="px-3 py-2 text-xs text-n-slate-10 italic text-center"
              >
                {{ t('TASKS.TAGS.EMPTY') }}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- DIREITA: prazo · prioridade · descrição · assignee · tipo · abrir -->
    <div
      class="flex items-center gap-2 pr-3 pb-2 flex-wrap"
      :class="[
        stacked ? '' : 'md:pb-0 md:flex-shrink-0 md:flex-nowrap',
        { 'md:pl-0': !stacked },
        hideContact ? 'pl-[52px]' : 'pl-14',
      ]"
    >
      <!-- Checklist badge X/Y -->
      <div
        v-if="checklistProgress"
        class="flex items-center gap-1 text-xs flex-shrink-0"
        :class="
          checklistProgress.done === checklistProgress.total
            ? 'text-n-teal-11'
            : 'text-n-slate-10'
        "
      >
        <span class="i-lucide-check-square size-3.5 flex-shrink-0" />
        <span>{{ checklistProgress.done }}/{{ checklistProgress.total }}</span>
      </div>

      <!-- Prazo: clique para editar -->
      <div
        v-if="task.status !== 'completed' || !hideCompletedInfo"
        class="relative"
        @click.stop
        @dblclick.stop
      >
        <button
          v-if="formattedDue"
          ref="datePickerAnchor"
          class="flex items-center gap-1 text-xs"
          :class="dueDateColor"
          @click="startEditDue"
        >
          <span class="i-lucide-calendar size-4.5" />
          {{ formattedDue }}
          <span
            v-if="task.recurrence_type"
            class="i-lucide-repeat size-3 text-n-slate-9 flex-shrink-0"
          />
        </button>
        <button
          v-else
          ref="datePickerAnchor"
          class="text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity"
          @click="startEditDue"
        >
          <span class="i-lucide-calendar size-4.5" />
        </button>
        <Teleport to="body">
          <div
            v-if="showDatePicker"
            ref="datePickerContainer"
            class="fixed z-[10010]"
            :style="{
              top: datePickerPos.top + 'px',
              left: datePickerPos.left + 'px',
            }"
          >
            <TaskDatePicker
              ref="datePickerRef"
              :model-value="datePickerValue"
              @update:model-value="commitDue"
              @clear-time="clearDueTime"
            />
          </div>
        </Teleport>
      </div>

      <!-- Badge de prioridade (movido para depois da data) -->
      <div
        v-if="task.status !== 'completed' || !hideCompletedInfo"
        class="relative"
      >
        <button
          class="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded transition-colors focus:outline-none"
          :class="
            showPriorityMenu && prioritySearch
              ? 'bg-n-alpha-2 border border-n-brand text-n-slate-10'
              : currentPriority && !currentPriority.bgColor
                ? [currentPriority.bg, currentPriority.text]
                : currentPriority
                  ? ''
                  : 'opacity-0 group-hover:opacity-60 bg-n-surface-2 text-n-slate-10'
          "
          :style="
            currentPriority?.bgColor && !(showPriorityMenu && prioritySearch)
              ? {
                  backgroundColor: currentPriority.bgColor,
                  color: currentPriority.textColor,
                }
              : {}
          "
          @click="togglePriorityMenu"
        >
          <template v-if="showPriorityMenu && prioritySearch">
            {{ prioritySearch }}
          </template>
          <template v-else>
            <span
              class="i-lucide-flag size-2.5 flex-shrink-0"
              :style="
                currentPriority?.textColor
                  ? { color: currentPriority.textColor }
                  : {}
              "
              :class="currentPriority?.dot"
            />
            <template v-if="!compactPriority">
              {{ currentPriority ? currentPriority.label : 'Prioridade' }}
            </template>
          </template>
        </button>
        <div
          v-if="showPriorityMenu"
          class="absolute top-7 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-36 overflow-hidden py-1"
          :class="priorityAlign === 'left' ? 'left-0' : 'right-0'"
          @click.stop
        >
          <button
            v-for="opt in filteredPriorityOptions"
            :key="opt.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              priorityNavigable.findIndex(i => i.id === opt.key) ===
              priorityHighlight
                ? opt.text
                  ? 'font-medium bg-n-alpha-1 ' + opt.text
                  : 'font-medium bg-n-alpha-1'
                : task.priority === opt.key
                  ? opt.text
                    ? 'font-medium ' + opt.text
                    : 'font-medium'
                  : 'text-n-slate-12'
            "
            :style="
              opt.textColor &&
              (priorityNavigable.findIndex(i => i.id === opt.key) ===
                priorityHighlight ||
                task.priority === opt.key)
                ? { color: opt.textColor }
                : {}
            "
            @click="selectPriority(opt.key)"
            @mouseenter="
              priorityHighlight = priorityNavigable.findIndex(
                i => i.id === opt.key
              )
            "
          >
            <span
              class="i-lucide-flag size-3 flex-shrink-0"
              :class="opt.dot || ''"
              :style="opt.textColor && !opt.dot ? { color: opt.textColor } : {}"
            />
            {{ opt.label }}
          </button>
          <p
            v-if="prioritySearch && !filteredPriorityOptions.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ $t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
        </div>
      </div>

      <!-- Ícone descrição: sempre visível quando tem conteúdo, com tooltip -->
      <div
        v-if="task.description"
        class="relative group/desc flex-shrink-0"
        @click.stop="emit('open', task)"
      >
        <span
          class="i-lucide-align-left size-4.5 text-n-slate-11 cursor-pointer"
        />
        <div
          class="absolute right-0 bottom-6 z-20 hidden group-hover/desc:block bg-n-surface-2 border border-n-weak text-n-slate-12 text-xs rounded px-2.5 py-1.5 w-52 whitespace-pre-line shadow-xl leading-relaxed pointer-events-none"
        >
          {{ descPreview }}
        </div>
      </div>
      <span
        v-else
        class="i-lucide-align-left size-4.5 text-n-slate-9 opacity-0 group-hover:opacity-30 transition-opacity flex-shrink-0"
      />

      <!-- Assignee: avatar principal + avatar secundário de checklist -->
      <div class="relative flex-shrink-0 flex items-center">
        <!-- Avatar secundário (agente de checklist distinto) -->
        <div
          v-if="checklistSecondaryAgent"
          class="size-5 rounded-full flex items-center justify-center border-2 border-n-background -mr-1.5 z-10 flex-shrink-0"
          :style="
            checklistSecondaryAgent.thumbnail
              ? {
                  backgroundImage: `url(${checklistSecondaryAgent.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : { backgroundColor: avatarColor(checklistSecondaryAgent) }
          "
          :title="checklistSecondaryAgent.name"
        >
          <span
            v-if="!checklistSecondaryAgent.thumbnail"
            class="text-[8px] font-bold text-white"
            >{{ initials(checklistSecondaryAgent.name) }}</span
          >
        </div>

        <!-- Avatar principal -->
        <div class="relative flex-shrink-0">
          <button
            class="size-7 rounded-full flex items-center justify-center hover:opacity-80 transition-all flex-shrink-0 focus:outline-none overflow-hidden"
            :style="
              showAssigneeMenu && assigneeSearch
                ? {}
                : task.assignee?.thumbnail
                  ? {
                      backgroundImage: `url(${task.assignee.thumbnail})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }
                  : task.assignee
                    ? { backgroundColor: avatarColor(task.assignee) }
                    : {}
            "
            :class="
              showAssigneeMenu && assigneeSearch
                ? 'bg-n-alpha-2 border border-n-brand'
                : !task.assignee
                  ? 'bg-n-surface-2'
                  : ''
            "
            :title="task.assignee?.name"
            @click="toggleAssigneeMenu"
          >
            <span
              v-if="showAssigneeMenu && assigneeSearch"
              class="text-[9px] font-medium text-n-slate-10 truncate px-0.5"
              >{{ assigneeSearch }}
            </span>
            <span
              v-else-if="!task.assignee?.thumbnail && task.assignee"
              class="text-[11px] font-bold text-white"
              >{{ initials(task.assignee.name) }}</span
            >
            <span
              v-else-if="!task.assignee"
              class="i-lucide-user-plus size-4 text-n-slate-9 flex-shrink-0"
            />
          </button>

          <!-- Dropdown de agentes -->
          <div
            v-if="showAssigneeMenu"
            class="absolute right-0 top-10 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-52 overflow-hidden py-1"
            @click.stop
          >
            <!-- Líder -->
            <button
              class="w-full flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap transition-colors focus:outline-none text-n-slate-12 hover:bg-n-alpha-1"
              :disabled="isResolvingLeader"
              @click="resolveLeader"
            >
              <span
                class="size-3.5 flex-shrink-0"
                :class="
                  isResolvingLeader
                    ? 'i-lucide-loader animate-spin text-n-slate-9'
                    : 'i-lucide-crown text-n-amber-9'
                "
              />
              <span :class="isResolvingLeader ? 'text-n-slate-9' : ''">
                {{ isResolvingLeader ? 'Resolvendo...' : 'Líder' }}
              </span>
            </button>
            <div v-if="!assigneeSearch" class="border-t border-n-weak my-1" />
            <button
              v-for="agent in filteredAssigneeAgents"
              :key="agent.id"
              class="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors focus:outline-none"
              :class="
                assigneeNavigable.findIndex(i => i.id === agent.id) ===
                assigneeHighlight
                  ? 'bg-n-alpha-1 text-n-brand font-medium'
                  : task.assignee?.id === agent.id
                    ? 'text-n-brand font-medium hover:bg-n-alpha-1'
                    : 'text-n-slate-12 hover:bg-n-alpha-1'
              "
              @click="selectAssignee(agent)"
              @mouseenter="
                assigneeHighlight = assigneeNavigable.findIndex(
                  i => i.id === agent.id
                )
              "
            >
              <div
                class="size-5 rounded-full flex-shrink-0"
                :style="
                  agent.thumbnail
                    ? {
                        backgroundImage: `url(${agent.thumbnail})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : { backgroundColor: avatarColor(agent.name) }
                "
              >
                <span
                  v-if="!agent.thumbnail"
                  class="text-[9px] font-bold text-white size-full flex items-center justify-center"
                  >{{ initials(agent.name) }}
                </span>
              </div>
              <span class="truncate">{{ agent.name }}</span>
              <span
                v-if="task.assignee?.id === agent.id"
                class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
              />
            </button>
            <p
              v-if="assigneeSearch && !assigneeNavigable.length"
              class="px-3 py-2 text-sm text-n-slate-10 italic"
            >
              {{ $t('TASKS.SMART_INPUT.NOT_FOUND') }}
            </p>
            <!-- Remover responsável -->
            <template v-if="assigneeNavigable.some(i => i.id === null)">
              <div v-if="!assigneeSearch" class="border-t border-n-weak my-1" />
              <button
                class="w-full flex items-center gap-2 px-3 py-2 text-sm whitespace-nowrap transition-colors focus:outline-none"
                :class="
                  assigneeHighlight ===
                  assigneeNavigable.findIndex(i => i.id === null)
                    ? 'bg-n-alpha-1 text-n-brand font-medium'
                    : 'text-n-slate-11 hover:bg-n-alpha-1'
                "
                @click="selectAssignee(null)"
                @mouseenter="
                  assigneeHighlight = assigneeNavigable.findIndex(
                    i => i.id === null
                  )
                "
              >
                <span class="i-lucide-user-x size-3.5 flex-shrink-0" />
                {{ t('TASKS.FIELDS.UNASSIGN') }}
              </button>
            </template>
          </div>
        </div>
      </div>

      <!-- Tipo de tarefa: ícone clicável -->
      <div class="relative flex-shrink-0">
        <button
          class="flex items-center justify-center size-9 rounded transition-colors focus:outline-none"
          :class="
            task.status === 'completed'
              ? 'cursor-default opacity-50'
              : showTypeMenu && typeSearch
                ? 'bg-n-alpha-2 border border-n-brand hover:bg-n-alpha-2'
                : 'hover:bg-n-alpha-2'
          "
          :title="currentType.label"
          :disabled="task.status === 'completed'"
          @click="task.status !== 'completed' && toggleTypeMenu($event)"
        >
          <span
            v-if="showTypeMenu && typeSearch"
            class="text-[9px] font-medium text-n-slate-10 truncate px-1"
            >{{ typeSearch }}
          </span>
          <span
            v-else
            class="size-7 text-n-slate-9"
            :class="[currentType.icon]"
          />
        </button>

        <!-- Dropdown de tipo -->
        <div
          v-if="showTypeMenu"
          class="absolute top-10 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-40 overflow-hidden py-1"
          :class="typeAlign === 'left' ? 'left-0' : 'right-0'"
          @click.stop
        >
          <button
            v-for="type in filteredTaskTypes"
            :key="type.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors focus:outline-none"
            :class="
              typeNavigable.findIndex(i => i.id === type.key) === typeHighlight
                ? 'text-n-brand font-medium bg-n-alpha-1'
                : type.key === task.task_type
                  ? 'text-n-brand font-medium'
                  : 'text-n-slate-12'
            "
            @click="selectType(type)"
            @mouseenter="
              typeHighlight = typeNavigable.findIndex(i => i.id === type.key)
            "
          >
            <span class="size-4 flex-shrink-0" :class="[type.icon]" />
            {{ type.label }}
            <span
              v-if="type.key === task.task_type"
              class="i-lucide-check size-3 text-n-brand ml-auto flex-shrink-0"
            />
          </button>
          <p
            v-if="typeSearch && !filteredTaskTypes.length"
            class="px-3 py-2 text-sm text-n-slate-10 italic"
          >
            {{ $t('TASKS.SMART_INPUT.NOT_FOUND') }}
          </p>
        </div>
      </div>

      <!-- Botão abrir: aparece no hover -->
      <button
        class="size-9 flex items-center justify-center rounded border border-n-container bg-n-background text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-n-alpha-2 flex-shrink-0"
        @click="openTask"
      >
        <span class="i-lucide-chevron-right size-6" />
      </button>
    </div>
  </div>
</template>
